/**
 * Settings → Betriebsparameter (HOF-708) — the farm's own settings, and the rules of their editors.
 *
 * **Every farm setting has its editor in this section.** A setting the farm may write is a
 * `SettingSpec` with a `write_permission` in `libs/tenant_config` (since HOF-708: every registered
 * one), listed by `GET /api/v1/farm/settings` and written through
 * `PUT /api/v1/farm/settings/{namespace}/{key}`. `FARM_PARAMETER_SETTINGS` names each one and
 * `PARAMETER_READ_PERMISSIONS` the seats that may open the section;
 * `backend/tests/unit/test_farm_parameter_editors_hof708.py` fails when either disagrees with the
 * registry. Design: `docs/architecture/TENANT_CONFIGURATION.md` § 4.3.
 */

/** `namespace.key` of every setting this section edits, in the order the pane shows them. */
export const FARM_PARAMETER_SETTINGS = [
    'labour_cost.fixed_shares',
    'labour.catalogue',
    'labour.access',
    'sources.declared',
    'sources.roles',
    'sources.precedence',
    'sources.reconciliation',
    'sources.matching',
    'farm.country',
] as const;

export type FarmParameterSetting = (typeof FARM_PARAMETER_SETTINGS)[number];

/** Any of these lets a seat read at least one setting (the union of the registry's read permissions). */
export const PARAMETER_READ_PERMISSIONS = [
    'farm.profile.write',
    'finance.read',
    'labour.read',
    'labour.self.read',
    'members.read',
] as const;

export const SETTINGS_LIST_URL = '/v1/farm/settings';

/** One item of `GET /api/v1/farm/settings`. */
export interface FarmSettingItem<T = unknown> extends SettingEnvelope<T> {
    effect: 'informational' | 'refuse_if_history' | 'next_write' | 'next_run';
    app_id: string | null;
    writable: boolean;
}

/** The registry's closed vocabularies (`hofsync_tenant_config.editor_options`). */
export interface EditorOptions {
    systems: string[];
    registers: string[];
    register_groups: string[];
    match_keys: string[];
    countries: string[];
    labour_access_modes: string[];
    employment_channels: string[];
    cost_object_kinds: string[];
    cost_object_refs: string[];
    activity_classes: string[];
}

export interface FarmSettingsList {
    items: FarmSettingItem[];
    options: EditorOptions;
}

export const settingId = (item: { namespace: string; key: string }): string => `${item.namespace}.${item.key}`;

export const settingUrl = (setting: string): string => `/v1/farm/settings/${setting.replace('.', '/')}`;

/** UDB's envelope, minus `updated_by`: the value, and the version a write must name. */
export interface SettingEnvelope<T> {
    namespace: string;
    key: string;
    value: T;
    version: number;
    updated_at: string | null;
    is_default: boolean;
}

export interface CropShare {
    crop: string;
    share: number;
}

export interface FixedShares {
    shares: CropShare[];
}

/** One editable row: the crop as typed and its share in percent, as typed. */
export interface ShareRow {
    crop: string;
    percent: string;
}

/** The registry's limits (`libs/tenant_config/settings.py::_validate_fixed_shares`). */
export const FIXED_SHARES_MAX_CROPS = 20;
export const CROP_MAX_LENGTH = 100;
/** The registry's tolerance on the share sum (`SHARE_SUM_TOLERANCE`), on the SAVED fractions. */
const SUM_TOLERANCE = 1e-6;

const foldCrop = (crop: string): string => crop.split(/\s+/).filter(Boolean).join(' ').toLocaleLowerCase('de-DE');

/** "12,5" or "12.5" → 12.5; anything that is not a finite number → NaN. */
export function parsePercent(text: string): number {
    const normalised = text.trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(normalised)) return Number.NaN;
    return Number(normalised);
}

/** A percentage as the fraction that is saved: six decimals, as the registry stores and sums it. */
export const shareOfPercent = (percent: number): number => Math.round(percent * 1e4) / 1e6;

export const formatPercent = (share: number): string =>
    new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4, useGrouping: false }).format(share * 100);

export const rowsFromValue = (value: FixedShares): ShareRow[] =>
    value.shares.map((s) => ({ crop: s.crop, percent: formatPercent(s.share) }));

export interface ShareCheck {
    /** The value to save, when the rows are valid. */
    value: FixedShares | null;
    /** The sum of the parseable percentages. */
    sumPercent: number;
    /** Why the rows cannot be saved, as a copy key (`settings.parameters.fixedShares.<problem>`). */
    problem: 'cropMissing' | 'cropTooLong' | 'cropTwice' | 'shareRange' | 'sumNot100' | 'tooMany' | null;
}

/**
 * The same rules the registry applies — on the fractions that will be SAVED (codex round 1: three
 * rows of 33,33336 % pass a check on the typed percentages but round to a sum the backend refuses).
 * A non-empty split must add up to exactly 100 %: under B1 a remainder would land on the hours.
 */
export function checkShareRows(rows: ShareRow[]): ShareCheck {
    const seen = new Set<string>();
    const shares: CropShare[] = [];
    let sumPercent = 0;
    let sumShares = 0;
    let problem: ShareCheck['problem'] = null;
    if (rows.length > FIXED_SHARES_MAX_CROPS) problem = 'tooMany';
    for (const row of rows) {
        const crop = row.crop.trim();
        const percent = parsePercent(row.percent);
        const share = Number.isFinite(percent) ? shareOfPercent(percent) : Number.NaN;
        if (Number.isFinite(percent)) sumPercent += percent;
        if (Number.isFinite(share)) sumShares += share;
        if (problem) continue;
        if (!crop) problem = 'cropMissing';
        else if (crop.length > CROP_MAX_LENGTH) problem = 'cropTooLong';
        else if (seen.has(foldCrop(crop))) problem = 'cropTwice';
        else if (!(share > 0 && share <= 1)) problem = 'shareRange';
        seen.add(foldCrop(crop));
        shares.push({ crop, share });
    }
    if (!problem && rows.length > 0 && Math.abs(sumShares - 1) > SUM_TOLERANCE) problem = 'sumNot100';
    return { value: problem ? null : { shares }, sumPercent, problem };
}

/* ------------------------------------------------------------------ the other settings' shapes */

export interface CatalogueActivity {
    key: string;
    label_de: string;
    label_ro: string;
    label_pl: string;
    activity_class?: string | null;
}

export interface CatalogueCostObject {
    key: string;
    kind: string;
    label_de: string;
    label_ro: string;
    label_pl: string;
    crop_cycle_id?: string | null;
    field_id?: string | null;
    cost_center_id?: string | null;
}

export interface LabourCatalogue {
    activities: CatalogueActivity[];
    cost_objects: CatalogueCostObject[];
}

export const CATALOGUE_MAX_ACTIVITIES = 12;
export const CATALOGUE_MAX_COST_OBJECTS = 30;
const CATALOGUE_KEY = /^[A-Za-z0-9_.-]{1,64}$/;

/** A new row's key, derived from its German label — the key never changes once saved. */
export function keyFromLabel(label: string, taken: Set<string>): string {
    const base =
        label
            .toLocaleLowerCase('de-DE')
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 56) || 'eintrag';
    let key = base;
    for (let n = 2; taken.has(key); n += 1) key = `${base}-${n}`;
    return key;
}

/**
 * The catalogue rule the registry applies (`_validate_labour_catalogue`), as a copy key or null. Only the
 * German name is the farm's to give: the save translates every blank Romanian/Polish one
 * (`backend/src/services/catalogue_translation.py`) before the registry sees the value.
 */
export function catalogueProblem(value: LabourCatalogue): 'labels' | 'keys' | 'linkId' | 'tooMany' | null {
    if (value.activities.length > CATALOGUE_MAX_ACTIVITIES || value.cost_objects.length > CATALOGUE_MAX_COST_OBJECTS) {
        return 'tooMany';
    }
    for (const items of [value.activities, value.cost_objects]) {
        const keys = new Set<string>();
        for (const item of items) {
            if (!item.label_de?.trim()) return 'labels';
            if (!CATALOGUE_KEY.test(item.key) || keys.has(item.key)) return 'keys';
            keys.add(item.key);
        }
    }
    const refs = ['crop_cycle_id', 'field_id', 'cost_center_id'] as const;
    for (const object of value.cost_objects) {
        if (refs.some((r) => object[r] != null && !String(object[r]).trim())) return 'linkId';
    }
    return null;
}

export interface LabourAccess {
    mode: string;
    channels?: string[];
}

export interface DeclaredSource {
    system: string;
    registers: string[];
}

/** How the farm's systems, registers and groups are named on screen. */
export const SYSTEM_LABELS: Record<string, string> = {
    wiberry: 'WiBerry',
    wibill: 'WiBill',
    myfarm24: 'MyFarm24',
    fruitforecast: 'FruitForecast',
    invekos_fnn: 'GAP-Antrag (InVeKoS)',
    hofsync: 'Hofsync',
    lodas: 'LODAS (Lohnabrechnung)',
};
export const REGISTER_LABELS: Record<string, string> = {
    field: 'Schläge',
    picking: 'Ernte',
    labour: 'Arbeitszeit',
    sales: 'Verkauf',
    bank: 'Bank',
    forecast: 'Ernteprognose',
};
export const GROUP_LABELS: Record<string, string> = {
    register_area: 'Fläche',
    register_agronomy: 'Anbau (Sorte, Boden, Pflanzung)',
};
export const MATCH_KEY_LABELS: Record<string, string> = {
    schlagnummer: 'Schlagnummer',
    'location.flik': 'FLIK',
    ear_tag: 'Ohrmarke',
};
export const labelOf = (labels: Record<string, string>, key: string): string => labels[key] ?? key;
